import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Container } from '@magnetic/container'
import { Flex } from '@magnetic/flex'
import { Text } from '@magnetic/text'
import { Input } from '@magnetic/input'
import { Spinner } from '@magnetic/spinner'
import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { listProjects, listApplications } from '@/lib/api'
import { FolderOpen, Package } from '@/lib/icons'

interface SearchResult {
  type: 'project' | 'application'
  id: string
  name: string
  projectId?: string
  namespace?: string
  url: string
}

export function HeaderSearch() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const { data: projectsData, isLoading: isLoadingProjects } = useConnectQuery(
    listProjects,
    {},
    { enabled: debouncedQuery.length >= 2 }
  )

  const { data: applicationsData, isLoading: isLoadingApplications } = useConnectQuery(
    listApplications,
    {},
    { enabled: debouncedQuery.length >= 2 }
  )

  const searchResults = useMemo(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) return []

    const query = debouncedQuery.toLowerCase()
    const results: SearchResult[] = []

    // Search projects
    if (projectsData?.projects) {
      projectsData.projects.forEach((project) => {
        if (
          project.name.toLowerCase().includes(query) ||
          (project.displayName && project.displayName.toLowerCase().includes(query)) ||
          project.namespace.toLowerCase().includes(query) ||
          (project.description && project.description.toLowerCase().includes(query))
        ) {
          results.push({
            type: 'project',
            id: project.id,
            name: project.displayName || project.name,
            namespace: project.namespace,
            url: `/projects/${project.id}`,
          })
        }
      })
    }

    // Search applications
    if (applicationsData?.applications) {
      applicationsData.applications.forEach((app) => {
        if (
          app.name.toLowerCase().includes(query) ||
          (app.displayName && app.displayName.toLowerCase().includes(query))
        ) {
          results.push({
            type: 'application',
            id: app.id,
            name: app.displayName || app.name,
            projectId: app.projectId,
            url: `/projects/${app.projectId}/applications/${app.id}`,
          })
        }
      })
    }

    // Limit results to 10
    return results.slice(0, 10)
  }, [debouncedQuery, projectsData, applicationsData])

  const isLoading = isLoadingProjects || isLoadingApplications

  const handleResultClick = (url: string) => {
    navigate(url)
    setSearchQuery('') // Clear search after navigation
  }

  return (
    <Container style={{ width: 500, maxHeight: 600, overflow: 'auto' }}>
      <Flex direction="vertical" gap={12} style={{ padding: 16 }}>
        <Input
          label="Search"
          placeholder="Search projects and applications..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus
        />

        {searchQuery.length > 0 && searchQuery.length < 2 && (
          <Flex justify="center" style={{ padding: 16 }}>
            <Text size="sm" color="secondary">
              Type at least 2 characters to search
            </Text>
          </Flex>
        )}

        {isLoading && debouncedQuery.length >= 2 && (
          <Flex justify="center" align="center" style={{ padding: 24 }}>
            <Spinner size="md" />
          </Flex>
        )}

        {!isLoading && debouncedQuery.length >= 2 && searchResults.length === 0 && (
          <Flex justify="center" style={{ padding: 24 }}>
            <Text size="sm" color="secondary">
              No results found for "{debouncedQuery}"
            </Text>
          </Flex>
        )}

        {!isLoading && searchResults.length > 0 && (
          <Flex direction="vertical" gap={4}>
            {searchResults.map((result) => (
              <Flex
                key={`${result.type}-${result.id}`}
                gap={12}
                align="center"
                style={{
                  padding: '12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  backgroundColor: 'var(--base-bg-weak-default)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--base-bg-weak-hover)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--base-bg-weak-default)'
                }}
                onClick={() => handleResultClick(result.url)}
              >
                {result.type === 'project' ? (
                  <FolderOpen size={20} weight="bold" color="var(--interact-icon-weak-default)" />
                ) : (
                  <Package size={20} weight="bold" color="var(--interact-icon-weak-default)" />
                )}

                <Flex direction="vertical" gap={2} style={{ flex: 1 }}>
                  <Text weight="semibold">{result.name}</Text>
                  <Text size="sm" color="secondary">
                    {result.type === 'project'
                      ? `Project • ${result.namespace}`
                      : 'Application'
                    }
                  </Text>
                </Flex>
              </Flex>
            ))}

            {searchResults.length >= 10 && (
              <Flex justify="center" style={{ paddingTop: 8 }}>
                <Text size="sm" color="tertiary">
                  Showing first 10 results
                </Text>
              </Flex>
            )}
          </Flex>
        )}

        {!searchQuery && (
          <Flex direction="vertical" gap={8} style={{ padding: '8px 0' }}>
            <Text size="sm" color="secondary">
              Search tips:
            </Text>
            <Text size="sm" color="tertiary">
              • Search by project or application name
            </Text>
            <Text size="sm" color="tertiary">
              • Search by namespace or description
            </Text>
            <Text size="sm" color="tertiary">
              • Click a result to navigate
            </Text>
          </Flex>
        )}
      </Flex>
    </Container>
  )
}
